'use client'

import * as React from 'react'
import { useOrganization } from '@clerk/nextjs'
import { useQueryClient } from '@tanstack/react-query'
import { Check, XCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { SettingsSection } from '@/features/settings-modal/settings-section'
import { useOrganizationId } from '@/features/organization/use-organization-id'
import { useRejectJoinRequestMutation } from '@/api/auth/team-detection/use-reject-join-request'
import { toast } from '@/features/toast/toast'
import {
    getJoinRequestRejectionToast,
    rejectJoinRequestWithMarker,
} from '@/features/auth/team-detection/reject-join-request-flow'
import {
    acceptMembershipRequestWithRefresh,
    getMembershipAcceptanceToast,
} from '@/features/auth/team-detection/accept-membership-request-flow'

/**
 * Admin surface for the Clerk membership-request flow. Reads
 * `useOrganization().membershipRequests` (populated when the org has a Verified Domain
 * with `automatic_suggestion` enrollment and a matching-domain signup clicked "Request
 * to join"). Accept / reject calls Clerk's `.accept()` / `.reject()`. Acceptance triggers
 * Clerk's `organization_joined` email; Clerk exposes no corresponding membership-request
 * rejection template, so rejection is communicated by our in-app state.
 *
 * Hidden entirely when there are no pending requests so the panel doesn't get noisy for
 * orgs that never receive any.
 */
export function PendingJoinRequestsSection() {
    const { organization, membershipRequests } = useOrganization({
        membershipRequests: { infinite: true, pageSize: 100 },
    })
    const organizationId = useOrganizationId()
    const qc = useQueryClient()
    const rejectJoinRequestMutation = useRejectJoinRequestMutation()
    const [pendingId, setPendingId] = React.useState<string | null>(null)

    const requests = membershipRequests?.data ?? []
    if (requests.length === 0) return null

    // Clerk's accept()/reject() don't mutate the cached request list, and accepting adds a
    // member the REST members query hasn't seen — refresh both so the members table and this
    // section update without a page reload.
    const refreshAfterResolved = async () => {
        await Promise.all([
            membershipRequests?.revalidate?.(),
            qc.invalidateQueries({ queryKey: ['organization', organizationId, 'members'] }),
        ])
    }

    const handleAccept = async (request: (typeof requests)[number]) => {
        setPendingId(request.id)
        try {
            const outcome = await acceptMembershipRequestWithRefresh({
                membershipRequestId: request.id,
                clerkOrganizationId: organization?.id,
                acceptInClerk: () => request.accept(),
                readAcceptedInClerk: () => request.reload(),
                refreshRequests: refreshAfterResolved,
            })
            const notification = getMembershipAcceptanceToast(outcome)
            if (notification.kind === 'success') toast.success(notification.message)
            else toast.warning(notification.message)
        } catch (error) {
            toast.error(error instanceof Error ? error.message : 'Could not accept the request')
        } finally {
            setPendingId(null)
        }
    }

    const handleReject = async (request: (typeof requests)[number]) => {
        setPendingId(request.id)
        try {
            const requesterClerkUserId = request.publicUserData?.userId
            const requesterEmail = request.publicUserData?.identifier
            if (!requesterEmail || !organization?.id) {
                throw new Error('Could not identify the requester — reload the page and try again')
            }

            const outcome = await rejectJoinRequestWithMarker({
                membershipRequestId: request.id,
                clerkOrganizationId: organization.id,
                rejectInClerk: () => request.reject(),
                readRejectedInClerk: () => request.reload(),
                recordMarker: (rejectedRequest) =>
                    rejectJoinRequestMutation.mutateAsync({
                        requesterClerkUserId,
                        requesterEmail,
                        membershipRequestId: rejectedRequest.id,
                        clerkOrganizationId: rejectedRequest.organizationId,
                    }),
                refreshRequests: refreshAfterResolved,
            })
            const notification = getJoinRequestRejectionToast(outcome)
            if (notification.kind === 'success') toast.success(notification.message)
            else toast.warning(notification.message)
        } catch (error) {
            toast.error(error instanceof Error ? error.message : 'Could not reject the request')
        } finally {
            setPendingId(null)
        }
    }

    return (
        <SettingsSection title="Pending join requests">
            <div className="rounded-lg border overflow-hidden">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>Email</TableHead>
                            <TableHead>Requested</TableHead>
                            <TableHead className="w-[180px] text-right pr-4">Actions</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {requests.map((request) => (
                            <TableRow key={request.id} className="hover:bg-transparent">
                                <TableCell className="text-sm">{request.publicUserData?.identifier ?? '—'}</TableCell>
                                <TableCell className="text-sm text-muted-foreground">
                                    {new Date(request.createdAt).toLocaleDateString()}
                                </TableCell>
                                <TableCell className="text-right pr-4">
                                    <div className="flex items-center gap-2 justify-end">
                                        <Button
                                            variant="ds2-secondary"
                                            size="ds2"
                                            onClick={() => handleReject(request)}
                                            disabled={pendingId !== null}
                                        >
                                            <XCircle className="h-3.5 w-3.5 mr-1" />
                                            Reject
                                        </Button>
                                        <Button
                                            variant="ds2-primary"
                                            size="ds2"
                                            onClick={() => handleAccept(request)}
                                            disabled={pendingId !== null}
                                        >
                                            <Check className="h-3.5 w-3.5 mr-1" />
                                            Accept
                                        </Button>
                                    </div>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
            {membershipRequests?.hasNextPage && (
                <Button
                    variant="ds2-secondary"
                    size="ds2"
                    onClick={() => membershipRequests.fetchNext()}
                    disabled={membershipRequests.isFetching || pendingId !== null}
                >
                    {membershipRequests.isFetching ? 'Loading…' : 'Load more requests'}
                </Button>
            )}
        </SettingsSection>
    )
}
